import type { ModuleTestSessionAdmin } from "./common.interfaces";
import {
  buildModuleTestMultipleChoiceDisplay,
  type ModuleTestAnswerOptionRow,
} from "./moduleTestAnswerDisplay";

/** Timeline event for module test report (add-admin-module-test-report + timeline parity). */
export type ModuleTestTimelineEvent =
  | { type: "SessionStarted"; at: string }
  | {
      type: "AnswerSubmitted";
      at: string;
      exerciseId: string;
      isCorrect: boolean;
      sequence: number;
      /** Rich display for multiple_choice when parse succeeds. */
      detail?: {
        exerciseTitle?: string;
        questionText?: string;
        options: ModuleTestAnswerOptionRow[];
      };
    }
  | { type: "TestCompleted"; at: string; scorePercent: number | null; passed: boolean | null };

export function buildModuleTestTimeline(session: ModuleTestSessionAdmin): ModuleTestTimelineEvent[] {
  const events: ModuleTestTimelineEvent[] = [];
  events.push({ type: "SessionStarted", at: session.createdAt });
  session.answers.forEach((a, i) => {
    const sequence = i + 1;
    const ex = a.exercise;
    let detail:
      | { exerciseTitle?: string; questionText?: string; options: ModuleTestAnswerOptionRow[] }
      | undefined;

    if (ex && String(ex.type).toLowerCase().includes("multiple")) {
      const mc = buildModuleTestMultipleChoiceDisplay(a);
      if (mc.options.length > 0) {
        detail = {
          exerciseTitle: mc.exerciseTitle,
          questionText: mc.questionText,
          options: mc.options,
        };
      }
    }

    events.push({
      type: "AnswerSubmitted",
      at: a.answeredAt,
      exerciseId: a.exerciseId,
      isCorrect: a.isCorrect,
      sequence,
      ...(detail ? { detail } : {}),
    });
  });
  if (session.finished && session.completedAt) {
    events.push({
      type: "TestCompleted",
      at: session.completedAt,
      scorePercent: session.scorePercent ?? null,
      passed: session.passed ?? null,
    });
  }
  return events;
}

/** Performance item for module test report: one answer with summary. */
export interface ModuleTestPerformanceItem {
  exerciseId: string;
  title: string | null;
  isCorrect: boolean;
  answeredAt: string;
  sequence: number;
}

export function buildModuleTestPerformance(session: ModuleTestSessionAdmin): ModuleTestPerformanceItem[] {
  return session.answers.map((a, i) => ({
    exerciseId: a.exerciseId,
    title: a.exercise?.title ?? null,
    isCorrect: a.isCorrect,
    answeredAt: a.answeredAt,
    sequence: i + 1,
  }));
}
